import { getLogger } from '@logtape/logtape';
import { randomId } from '@mantine/hooks';
import { create } from 'zustand';

import { SERIES_AUTO_SUGGESTION_DEFAULTS } from '@/constants/series-auto-suggestion.ts';

import { EnrichedCustomer, EnrichedLocation } from '@/models/EnrichedPrismicDocuments.ts';
import { GenericPatient } from '@/models/GenericPatient.ts';
import {
    Appointment,
    AppointmentBookStatus,
    AppointmentCollectionItem,
    CollectionConfiguration,
    CollectionOccurrence,
    CollectionStep,
    DailyAppointment,
    IntervalType,
    PlacedAppointment,
    SeriesPlacementConfig,
    SeriesStateSnapshot,
} from '@/models/appointment/Appointment.ts';
import { AppointmentBookingFormValues } from '@/models/appointment/AppointmentBookingFormValues.ts';
import type { ChainAvailabilityItem } from '@/models/appointment/AppointmentChainAvailability.ts';
import {
    ChainBookingStep,
    ChainPlanningMode,
    type ChainSelection,
} from '@/models/appointment/AppointmentChainConfig.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { EnrichedTask } from '@/models/doc-cirrus/DCTask.ts';
import type { InsuranceType } from '@/models/insurance.ts';

import { getInstanceConfig } from '@/utils/configUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';

const logger = getLogger(['store', 'booking']);

// Define types for each appointment status
export interface EnrichedAppointmentWithBookingStatus {
    form_id: string;
    status: AppointmentBookStatus;
    form_data: AppointmentBookingFormValues; // TODO: store the form data to be able to recover it
    appointment?: Appointment;
    error?: string;
    booking_order?: number;
}

export interface CollectionBookingSummary {
    type: 'series' | 'chain';
    appointments: EnrichedAppointmentWithBookingStatus[];
    booking_order: number;
}

// Define batch structure
export interface BookingBatch {
    customer: EnrichedCustomer;
    location: EnrichedLocation;
    batch_id: string;
    notification_id: string;
    patient: GenericPatient;
    enriched_appointments: EnrichedAppointmentWithBookingStatus[];
    created_at: number;
}

interface BookModeParams {
    customer: EnrichedCustomer;
    location: EnrichedLocation;
    patient: GenericPatient;
    prefilled_values?: PrefilledFormValues;
}

interface EditModeParams {
    customer: EnrichedCustomer;
    location: EnrichedLocation;
    patient: GenericPatient;
    appointment: DailyAppointment;
    prefilled_values?: PrefilledFormValues;
    isSingleCollectionEdit?: boolean;
    editingCollectionId?: string;
}

// Define Zustand store
interface BookingStore {
    is_open: boolean;
    appointments_form_draft: (AppointmentBookingFormValues & { id: string })[]; // Draft that exist only in the scheduler
    initial_form_values?: Partial<AppointmentBookingFormValues>;
    current_appointment_or_draft_id: string | undefined; // Form that is being edited, the other ones are collapsed
    is_last_form_valid: boolean;
    is_booking_loading: boolean;
    isCollectionSubmitting: boolean;
    collection_bookings: CollectionBookingSummary[];
    booking_order_counter: number;
    editing_appointment?: DailyAppointment;
    current_batch_id?: string;
    current_patient?: GenericPatient;
    batches: Map<string, BookingBatch>;
    patient_duplication_allowed: boolean;
    isSeriesMode: boolean;
    isSingleCollectionEdit: boolean;
    editingCollectionId?: string;
    collectionStep: CollectionStep;
    collection_configuration?: CollectionConfiguration;
    collection_occurrences: CollectionOccurrence[];
    collection_suggestion_history: CollectionOccurrence[][];
    is_validating_insurance: boolean;
    isSeriesPlacementMode: boolean;
    seriesPlacementConfig: SeriesPlacementConfig | undefined;
    seriesPlacedAppointments: PlacedAppointment[];
    collectionPastAppointments: AppointmentCollectionItem[];
    collectionOriginalFutureSlots: string[];
    openSchedulerEditMode: (params: EditModeParams) => void;
    openSchedulerBookMode: (params: BookModeParams) => void;
    openSchedulerViewMode: (batch_id: string) => void; // from notification
    reopenInSeriesMode: (patient: GenericPatient, seriesState: SeriesStateSnapshot) => void;
    resetAllData: () => void;
    addNewDefaultDraft: (prefilled_values?: PrefilledFormValues) => void;
    setIsLastFormValid: (is_valid: boolean) => void;
    getCurrentBatch: () => BookingBatch | undefined;
    getBatchById: (batch_id: string) => BookingBatch | undefined;
    createNewBatch: (
        customer: EnrichedCustomer,
        location: EnrichedLocation,
        patient: GenericPatient,
        appointments: EnrichedAppointmentWithBookingStatus[]
    ) => void;
    addAppointmentsToBatch: (
        batch_id: string,
        notification_id: string,
        new_appointments: EnrichedAppointmentWithBookingStatus[]
    ) => void;
    updateAppointmentBookingStatus: (
        batch_id: string,
        form_id: string,
        status: AppointmentBookStatus,
        appointment?: Appointment,
        error?: string
    ) => void;
    removeBatch: (batch_id: string) => void;
    completeCollectionBooking: (
        type: CollectionBookingSummary['type'],
        appointments: EnrichedAppointmentWithBookingStatus[]
    ) => void;
    booking_tasks: EnrichedTask[];
    addBookingTask: (task: EnrichedTask) => void;
    removeBookingTask: (task: EnrichedTask) => void;
    getAppointmentFromBatchById: (appointment_id: string, batch_id: string) => Appointment | undefined;
    setCollectionPastAppointments: (appointments: AppointmentCollectionItem[]) => void;
    setCollectionOriginalFutureSlots: (slots: string[]) => void;
    setCollectionConfiguration: (config: CollectionConfiguration) => void;
    setIsSeriesMode: (value: boolean) => void;
    chain_selection: ChainSelection;
    is_returning_patient: boolean;
    setIsReturningPatient: (value: boolean) => void;
    is_chain_mode: boolean;
    setIsChainMode: (value: boolean) => void;
    chain_booking_step: ChainBookingStep;
    chain_availability: ChainAvailabilityItem[];
    chain_suggestion_history: ChainAvailabilityItem[][];
    setChain: (chain_id: string | undefined, insurance_type: InsuranceType | undefined) => void;
    setChainStartDate: (date: string) => void;
    clearChainForPatientType: () => void;
    toggleChainStepRemoval: (order: number) => void;
    setChainCalendar: (order: number, calendar_id: string | undefined) => void;
    setChainMainPractitioner: (practitioner_id: string | undefined) => void;
    resetChainSelection: () => void;
    setChainBookingStep: (step: ChainBookingStep) => void;
    setChainPlanningMode: (mode: ChainPlanningMode) => void;
    setChainAvailability: (items: ChainAvailabilityItem[]) => void;
    setChainSuggestionHistory: (history: ChainAvailabilityItem[][]) => void;
}

const createDefaultChainSelection = (): ChainSelection => ({
    chain_id: undefined,
    insurance_type: undefined,
    start_date: dayjs().format('YYYY-MM-DD'),
    planning_mode: ChainPlanningMode.AUTO,
    removed_orders: [],
    calendar_selections: {},
    main_practitioner_id: undefined,
});

const createChainSelectionKeepingPreferences = (current: ChainSelection): ChainSelection => ({
    ...createDefaultChainSelection(),
    start_date: current.start_date,
    planning_mode: current.planning_mode ?? ChainPlanningMode.AUTO,
});

const chainResetPatch = {
    chain_availability: [] as ChainAvailabilityItem[],
    chain_suggestion_history: [] as ChainAvailabilityItem[][],
    chain_booking_step: ChainBookingStep.CONFIGURE,
};

export type PrefilledFormValues = Partial<Omit<AppointmentBookingFormValues, 'pvs_provider'>> & {
    id?: string;
    pvs_provider: PVSProvider;
};

// Create Zustand store
export const useBookingStore = create<BookingStore>((set, get) => ({
    is_open: false,
    appointments_form_draft: [],
    initial_form_values: undefined,
    current_appointment_or_draft_id: undefined,
    is_last_form_valid: false,
    is_booking_loading: false,
    isCollectionSubmitting: false,
    collection_bookings: [],
    booking_order_counter: 0,
    editing_appointment: undefined,
    current_batch_id: undefined,
    current_patient: undefined,
    batches: new Map<string, BookingBatch>(),
    booking_tasks: [],
    patient_duplication_allowed: false,
    isSeriesMode: false,
    isSingleCollectionEdit: false,
    editingCollectionId: undefined,
    collectionStep: CollectionStep.CONFIG,
    collection_configuration: undefined,
    collection_occurrences: [],
    collection_suggestion_history: [],
    is_validating_insurance: false,
    isSeriesPlacementMode: false,
    seriesPlacementConfig: undefined,
    seriesPlacedAppointments: [],
    collectionPastAppointments: [],
    chain_selection: createDefaultChainSelection(),
    is_chain_mode: false,
    ...chainResetPatch,
    collectionOriginalFutureSlots: [],
    is_returning_patient: true,
    /**
     * To be used to set the context to an existing batch
     * @param batch_id existing batch id
     */
    openSchedulerViewMode: (batch_id): void => {
        const batch: BookingBatch | undefined = get().batches.get(batch_id);

        if (!batch) return;

        set(() => ({
            current_batch_id: batch.batch_id,
            current_patient: batch.patient,
        }));
    },

    openSchedulerBookMode: (params: BookModeParams): void => {
        const instance_config = getInstanceConfig(params.patient.instance_id ?? '');
        if (!instance_config) return;

        set(() => ({
            current_patient: params.patient,
        }));

        get().addNewDefaultDraft({
            ...params.prefilled_values,
            pvs_provider: instance_config.pvs_provider,
        });

        get().createNewBatch(params.customer, params.location, params.patient, []);
    },

    openSchedulerEditMode: (params: EditModeParams): void => {
        const isEditingSeries = !!params.editingCollectionId;
        set(() => ({
            current_patient: params.patient,
            editing_appointment: params.appointment,
            isSingleCollectionEdit: params.isSingleCollectionEdit ?? false,
            editingCollectionId: params.editingCollectionId,
            isSeriesMode: isEditingSeries,
        }));
        get().addNewDefaultDraft(params.prefilled_values);
        get().createNewBatch(params.customer, params.location, params.patient, []);
    },

    reopenInSeriesMode: (_patient, seriesState): void => {
        // Restore series-specific state. openSchedulerBookMode (called before this)
        // appended a blank draft — trim back to the original and re-focus it.
        set((state) => ({
            isSeriesMode: seriesState.isSeriesMode,
            collectionStep: seriesState.collectionStep,
            collection_configuration: seriesState.collection_configuration,
            collectionPastAppointments: seriesState.collectionPastAppointments,
            collection_occurrences: seriesState.collection_occurrences,
            appointments_form_draft: state.appointments_form_draft.slice(0, 1),
            current_appointment_or_draft_id: state.appointments_form_draft[0]?.id,
        }));
    },

    resetAllData: (): void => {
        const state = get();

        // Guard: when entering placement mode, closeAllModals() triggers this via
        // the scheduler's onClose callback. We must preserve placement + scheduler
        // state so the user can return to the series list after placing appointments.
        // This guard is necessary because we cannot change the modal's onClose callback.
        if (state.isSeriesPlacementMode) {
            set(() => ({
                is_open: false,
                is_last_form_valid: false,
                is_booking_loading: false,
                isCollectionSubmitting: false,
                current_batch_id: undefined,
                editing_appointment: undefined,
                patient_duplication_allowed: false,
                isSingleCollectionEdit: false,
                is_validating_insurance: false,
                chain_selection: createDefaultChainSelection(),
                ...chainResetPatch,
            }));
            return;
        }

        set(() => ({
            is_open: false,
            appointments_form_draft: [],
            current_appointment_or_draft_id: undefined,
            initial_form_values: undefined,
            is_last_form_valid: false,
            is_booking_loading: false,
            isCollectionSubmitting: false,
            collection_bookings: [],
            booking_order_counter: 0,
            current_batch_id: undefined,
            editing_appointment: undefined,
            current_patient: undefined,
            booking_tasks: [],
            patient_duplication_allowed: false,
            isSeriesMode: false,
            isSingleCollectionEdit: false,
            editingCollectionId: undefined,
            collectionStep: CollectionStep.CONFIG,
            collection_configuration: undefined,
            collection_occurrences: [],
            collection_suggestion_history: [],
            is_validating_insurance: false,
            isSeriesPlacementMode: false,
            seriesPlacementConfig: undefined,
            seriesPlacedAppointments: [],
            collectionPastAppointments: [],
            chain_selection: createDefaultChainSelection(),
            is_chain_mode: false,
            ...chainResetPatch,
            collectionOriginalFutureSlots: [],
            is_returning_patient: true,
        }));
    },

    addNewDefaultDraft: (prefilled_values?: PrefilledFormValues): void =>
        set((state) => {
            const id = prefilled_values?.id ?? randomId();
            const new_default_form_values = {
                ...prefilled_values,
                date_time: prefilled_values?.date_time ?? new Date().toISOString(),
            } as AppointmentBookingFormValues;

            return {
                appointments_form_draft: [...state.appointments_form_draft, { id, ...new_default_form_values }],
                current_appointment_or_draft_id: id,
                initial_form_values: { ...state.initial_form_values, ...prefilled_values },
                is_last_form_valid: false,
            };
        }),

    setIsLastFormValid: (is_valid: boolean): void => set(() => ({ is_last_form_valid: is_valid })),

    getAppointmentFromBatchById: (appointment_id: string, batch_id: string): Appointment | undefined => {
        return get()
            .getBatchById(batch_id)
            ?.enriched_appointments.find((apt) => apt.appointment?.id === appointment_id)?.appointment;
    },

    getCurrentBatch: (): BookingBatch | undefined => {
        return get().batches.get(get().current_batch_id ?? '');
    },

    getBatchById: (batch_id: string): BookingBatch | undefined => {
        return get().batches.get(batch_id);
    },

    createNewBatch: (customer, location, patient, appointments): void => {
        const new_batch_id = randomId();
        set((state) => {
            const updated_batches = new Map(state.batches);
            updated_batches.set(new_batch_id, {
                batch_id: new_batch_id,
                customer: customer,
                location: location,
                notification_id: '',
                patient: patient,
                enriched_appointments: appointments,
                created_at: Date.now(),
            });

            return {
                current_batch_id: new_batch_id,
                batches: updated_batches,
            };
        });
    },

    /**
     * Add additional appointments to an existing batch.
     */
    addAppointmentsToBatch: (batch_id, notification_id, new_appointments): void =>
        set((state) => {
            const updated_batches = new Map(state.batches);
            const batch = updated_batches.get(batch_id);
            // If the batch doesn't exist, don't modify state
            if (!batch || !batch.enriched_appointments) {
                logger.debug('No batch found for batch_id {batchId}', { batchId: batch_id });
                return state;
            }

            let counter = state.booking_order_counter;
            const stamped = new_appointments.map((apt) => ({ ...apt, booking_order: (counter += 1) }));

            batch.notification_id = notification_id;
            batch.enriched_appointments = [...batch.enriched_appointments, ...stamped];

            updated_batches.set(batch_id, batch);
            return { batches: updated_batches, booking_order_counter: counter };
        }),

    updateAppointmentBookingStatus: (batch_id, form_id, status, appointment, error): void =>
        set((state) => {
            const updated_batches = new Map(state.batches);
            const batch = updated_batches.get(batch_id);
            if (!batch) return state;

            batch.enriched_appointments = batch.enriched_appointments.map((apt) =>
                apt.form_id === form_id ? { ...apt, status, appointment, error } : apt
            );

            updated_batches.set(batch_id, batch);
            return { batches: updated_batches };
        }),

    // Remove only if no need to retrieve it anymore.
    // Batch are used to retrieve scheduler context from a notification.
    removeBatch: (batch_id): void =>
        set((state) => {
            const updated_batches = new Map(state.batches);
            updated_batches.delete(batch_id);
            return { batches: updated_batches };
        }),

    completeCollectionBooking: (type, appointments): void =>
        set((state) => ({
            collection_bookings: [
                ...state.collection_bookings,
                { type: type, appointments: appointments, booking_order: state.booking_order_counter + 1 },
            ],
            booking_order_counter: state.booking_order_counter + 1,
            appointments_form_draft: [],
            current_appointment_or_draft_id: undefined,
            is_booking_loading: false,
            isCollectionSubmitting: false,
            isSeriesMode: false,
            is_chain_mode: false,
        })),

    addBookingTask: (task): void =>
        set((state) => {
            const matches = (t: EnrichedTask): boolean => t.id === task.id && t.instance_id === task.instance_id;
            const exists = state.booking_tasks.some(matches);
            return {
                booking_tasks: exists
                    ? state.booking_tasks.map((t) => (matches(t) ? task : t))
                    : [...state.booking_tasks, task],
            };
        }),

    // Match on composite (id, instance_id) so a same-id task on a different instance isn't dropped.
    removeBookingTask: (task): void =>
        set((state) => ({
            booking_tasks: state.booking_tasks.filter((t) => !(t.id === task.id && t.instance_id === task.instance_id)),
        })),

    setCollectionPastAppointments: (appointments: AppointmentCollectionItem[]): void => {
        set(() => ({ collectionPastAppointments: appointments }));
    },
    setCollectionOriginalFutureSlots: (slots: string[]): void => {
        set(() => ({ collectionOriginalFutureSlots: slots }));
    },
    setCollectionConfiguration: (config: CollectionConfiguration): void => {
        set(() => ({ collection_configuration: config }));
    },

    setChain: (chain_id: string | undefined, insurance_type: InsuranceType | undefined): void => {
        set((state) => ({
            chain_selection: {
                ...createChainSelectionKeepingPreferences(state.chain_selection),
                chain_id: chain_id,
                insurance_type: insurance_type,
            },
            ...chainResetPatch,
        }));
    },

    setChainStartDate: (date: string): void => {
        set((state) => ({
            chain_selection: { ...state.chain_selection, start_date: date },
        }));
    },

    setIsReturningPatient: (value: boolean): void => {
        set(() => ({ is_returning_patient: value }));
    },

    clearChainForPatientType: (): void => {
        set((state) => ({
            chain_selection: {
                ...createChainSelectionKeepingPreferences(state.chain_selection),
                insurance_type: state.chain_selection.insurance_type,
                is_chain_cleared_by_patient_type: true,
            },
            ...chainResetPatch,
        }));
    },

    toggleChainStepRemoval: (order: number): void => {
        set((state) => {
            const current = state.chain_selection.removed_orders;
            const next = current.includes(order) ? current.filter((o) => o !== order) : [...current, order];
            return {
                chain_selection: { ...state.chain_selection, removed_orders: next },
            };
        });
    },

    setChainCalendar: (order: number, calendar_id: string | undefined): void => {
        set((state) => {
            const { [order]: removed, ...rest } = state.chain_selection.calendar_selections;
            void removed;
            return {
                chain_selection: {
                    ...state.chain_selection,
                    calendar_selections: calendar_id ? { ...rest, [order]: calendar_id } : rest,
                },
            };
        });
    },

    setChainMainPractitioner: (practitioner_id: string | undefined): void => {
        set((state) => ({
            chain_selection: { ...state.chain_selection, main_practitioner_id: practitioner_id },
        }));
    },

    resetChainSelection: (): void => {
        set(() => ({
            chain_selection: createDefaultChainSelection(),
            ...chainResetPatch,
        }));
    },

    setChainBookingStep: (step: ChainBookingStep): void => {
        set(() => ({ chain_booking_step: step }));
    },

    setChainPlanningMode: (mode: ChainPlanningMode): void => {
        set((state) => ({
            chain_selection: { ...state.chain_selection, planning_mode: mode },
        }));
    },

    setChainAvailability: (items: ChainAvailabilityItem[]): void => {
        set(() => ({ chain_availability: items }));
    },

    setChainSuggestionHistory: (history: ChainAvailabilityItem[][]): void => {
        set(() => ({ chain_suggestion_history: history }));
    },

    setIsChainMode: (value: boolean): void => {
        // Reset on every toggle (including entering) so a new chain starts at CONFIGURE, not the previous chain's step.
        set(() => ({
            is_chain_mode: value,
            chain_selection: createDefaultChainSelection(),
            ...chainResetPatch,
        }));
    },

    setIsSeriesMode: (value: boolean): void => {
        // Reset on enter so a new series starts at CONFIG, not the previous series' LIST step; on exit clear only mode + config.
        set(() =>
            value
                ? {
                      isSeriesMode: true,
                      collection_configuration: {
                          start_date: dayjs().format('YYYY-MM-DD'),
                          interval_type: IntervalType.WEEKLY,
                          interval_frequency: 1,
                          number_of_appointments: 6,
                          ...SERIES_AUTO_SUGGESTION_DEFAULTS,
                      },
                      collectionStep: CollectionStep.CONFIG,
                      collection_occurrences: [],
                      collection_suggestion_history: [],
                      isSeriesPlacementMode: false,
                      seriesPlacementConfig: undefined,
                      seriesPlacedAppointments: [],
                      collectionPastAppointments: [],
                      collectionOriginalFutureSlots: [],
                  }
                : { isSeriesMode: false, collection_configuration: undefined }
        );
    },
}));

/** Selector: resolves the currently active form draft by `current_appointment_or_draft_id`, falling back to the first draft. */
export const selectCurrentForm = (
    state: Pick<BookingStore, 'appointments_form_draft' | 'current_appointment_or_draft_id'>
): BookingStore['appointments_form_draft'][number] | undefined =>
    state.appointments_form_draft.find((d) => d.id === state.current_appointment_or_draft_id) ??
    state.appointments_form_draft[0];
